import React from "react";
import { Text } from "recharts";

import * as Colors from "../Colors";

export function sortData(data, options) {
  const labelNumerical = data.every((val) => !isNaN(val.name));

  switch (options.sort) {
    case "ASCENDING":
      if (options.type === "BOX_PLOT") {
        return data.sort((a, b) => a.median - b.median);
      }
      return data.sort((a, b) => a.value - b.value);
    case "DESCENDING":
      if (options.type === "BOX_PLOT") {
        return data.sort((a, b) => b.median - a.median);
      }
      return data.sort((a, b) => b.value - a.value);
    case "LABEL_DESCENDING":
      if (!labelNumerical) {
        return data.sort((a, b) => (a.name > b.name ? -1 : 1));
      }
      return data.sort((a, b) => b.name - a.name);
    case "LABEL_ASCENDING":
      if (!labelNumerical) {
        return data.sort((a, b) => (b.name > a.name ? -1 : 1));
      }
      return data.sort((a, b) => a.name - b.name);
    default:
      return data;
  }
}

// https://github.com/30-seconds/30-seconds-of-code/blob/master/snippets/median.md
const median = (arr) => {
  const mid = Math.floor(arr.length / 2),
    nums = [...arr].sort((a, b) => a - b);
  return arr.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
};

export function getDistinct(data, accessor) {
  const distinctValues = new Set();
  data.forEach((d) => {
    distinctValues.add(d[accessor]);
  });
  return distinctValues;
}

export function getLongestTick(data, accessor) {
  return data.reduce((longest, current) => {
    return current[accessor].length > longest.length
      ? current[accessor]
      : longest;
  }, "");
}

export function formatLabelList(value, label, options) {
  const decimals = options.decimals ?? 0;
  let formattedValue = Number.parseFloat(value).toFixed(decimals);

  if (label && label.toLowerCase().includes("percentage")) {
    formattedValue += "%";
  }
  return formattedValue;
}

function StartLabel(props) {
  const { x, y, dy, width, text } = props;
  // If you have more than one \n everything after the second \n will be ignored
  // If you need this functionality just add a map for every element after you split and add some amount to y
  // But I don't need it so I'm leaving this as is
  const startLabelSplit = text.split("\n");

  return (
    <g>
      <Text
        x={x}
        y={y}
        dy={dy}
        textAnchor="start"
        verticalAnchor="middle"
        fill={Colors.PURPLE}
        fontSize="1.3rem"
        width={width}
      >
        {startLabelSplit[0]}
      </Text>
      {startLabelSplit[1] && (
        <Text
          x={x}
          y={y + 15}
          dy={dy}
          textAnchor="start"
          verticalAnchor="middle"
          fill={Colors.PURPLE}
          fontSize="1.3rem"
          width={width}
        >
          {startLabelSplit[1]}
        </Text>
      )}
    </g>
  );
}

export function CustomXAxisLabel({
  label,
  startLabel,
  endLabel,
  viewBox,
  offset,
  dy,
}) {
  const { x, y, width, height } = viewBox;

  const mainLabelOffset = startLabel ? 20 : 0;

  return (
    <g>
      <Text
        x={width / 2 + x}
        y={y + height + mainLabelOffset}
        dy={offset + dy}
        textAnchor="middle"
        verticalAnchor="middle"
        fill={Colors.PURPLE}
        fontSize="1.4rem"
      >
        {label}
      </Text>
      {startLabel && (
        <StartLabel
          x={x}
          y={y + height}
          dy={offset + dy}
          width={width / 2 - 30}
          text={startLabel}
        />
      )}
      {startLabel && endLabel && (
        <Text
          x={width + x}
          y={y + height}
          dy={offset + dy}
          textAnchor="end"
          verticalAnchor="middle"
          fill={Colors.PURPLE}
          fontSize="1.3rem"
        >
          {endLabel}
        </Text>
      )}
      {endLabel && (
        <Text
          x={width / 2 + x}
          y={y + height}
          dy={offset + dy}
          textAnchor="middle"
          verticalAnchor="middle"
          fill={Colors.PURPLE}
          fontSize="2.4rem"
        >
          ⟶
        </Text>
      )}
    </g>
  );
}
